import React from "react";
import { STATUS_LABELS } from "./constants/adminConstants";
import "./css/adminStyles.css";

function AdminChangeDetail({ changeRequest }) {
  if (!changeRequest) {
    return <div className="AdminEmptyState">No change details available.</div>;
  }

  let changes = [];
  try {
    changes = typeof changeRequest.changes === "string"
      ? JSON.parse(changeRequest.changes)
      : changeRequest.changes || [];
  } catch (e) {
    changes = [];
  }

  const submitterNote = changes?.submitterNote || null;

  const renderChangeValue = (val) => {
    if (val === null || val === undefined) return <em style={{ color: "#94a3b8" }}>empty</em>;
    const str = typeof val === "object" ? JSON.stringify(val) : String(val);
    if (str.length > 80) return str.substring(0, 80) + "...";
    return str;
  };

  const renderChanges = () => {
    if (!changes || typeof changes !== "object") {
      return <div className="AdminEmptyState">No detailed change data available.</div>;
    }

    const filtered = { ...changes };
    delete filtered.submitterNote;

    // Profile change format: { field: { from: "...", to: "..." } }
    const isProfileFormat = Object.values(filtered).some(
      (v) => v && typeof v === "object" && "from" in v && "to" in v
    );
    if (isProfileFormat) {
      return Object.entries(filtered).map(([key, value]) => (
        <div key={key} className="AdminChangeItem">
          <span className="AdminChangeKey">{key}:</span>
          <span className="AdminChangeOld">{renderChangeValue(value.from)}</span>
          <span style={{ color: "#94a3b8", margin: "0 4px" }}>&rarr;</span>
          <span className="AdminChangeNew">{renderChangeValue(value.to)}</span>
        </div>
      ));
    }

    // Array format: [{ field: "...", oldValue: "...", newValue: "..." }]
    if (Array.isArray(changes)) {
      if (changes.length === 0) return <div className="AdminEmptyState">No changes recorded.</div>;
      return changes.map((change, index) => {
        const hasField = change.field || change.Field;
        const hasOld = change.oldValue !== undefined || change.Old !== undefined;
        const hasNew = change.newValue !== undefined || change.New !== undefined;
        const oldVal = change.oldValue !== undefined ? change.oldValue : change.Old;
        const newVal = change.newValue !== undefined ? change.newValue : change.New;
        const fieldName = hasField || `Change #${index + 1}`;
        return (
          <div key={index} className="AdminChangeItem">
            <span className="AdminChangeKey">{fieldName}:</span>
            {hasOld && <span className="AdminChangeOld">{renderChangeValue(oldVal)}</span>}
            {hasOld && hasNew && <span style={{ color: "#94a3b8", margin: "0 4px" }}>&rarr;</span>}
            {hasNew && <span className="AdminChangeNew">{renderChangeValue(newVal)}</span>}
          </div>
        );
      });
    }

    // Content update: { type: "content_update", sectionKey: "...", content: { ... } }
    if (filtered.type === "content_update") {
      const sectionName = filtered.sectionKey || (filtered.content ? Object.keys(filtered.content)[0] : "Unknown");
      return (
        <div className="AdminChangeItem">
          <span className="AdminChangeKey">Table:</span>
          <span className="AdminChangeNew">{sectionName}</span>
        </div>
      );
    }

    // Fallback: show object keys with their values (truncated)
    return Object.entries(filtered).map(([key, value]) => (
      <div key={key} className="AdminChangeItem">
        <span className="AdminChangeKey">{key}:</span>
        <span className="AdminChangeNew">{renderChangeValue(value)}</span>
      </div>
    ));
  };

  return (
    <div className="AdminChangeDetail">
      <h4 style={{ margin: "0 0 12px 0", color: "#1B2A4A", fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 600 }}>
        Request Details
      </h4>

      <div style={{ marginBottom: 12 }}>
        <p style={{ margin: "4px 0", fontSize: 13, color: "#64748b" }}>
          <strong>Description:</strong> {changeRequest.description}
        </p>
        {submitterNote && (
          <p style={{ margin: "4px 0", fontSize: 13, color: "#64748b" }}>
            <strong>Note:</strong> {submitterNote}
          </p>
        )}
        <p style={{ margin: "4px 0", fontSize: 13, color: "#64748b" }}>
          <strong>Submitted by:</strong> {changeRequest.staffFirstName} {changeRequest.staffLastName}
        </p>
        <p style={{ margin: "4px 0", fontSize: 13, color: "#64748b" }}>
          <strong>Status:</strong> {STATUS_LABELS[changeRequest.status] || changeRequest.status}
        </p>
        {changeRequest.managerNote && (
          <p style={{ margin: "4px 0", fontSize: 13, color: "#64748b" }}>
            <strong>Note:</strong> {changeRequest.managerNote}
          </p>
        )}
        {changeRequest.adminNote && (
          <p style={{ margin: "4px 0", fontSize: 13, color: "#64748b" }}>
            <strong>Note:</strong> {changeRequest.adminNote}
          </p>
        )}
      </div>

      <h4 style={{ margin: "12px 0 8px", color: "#1B2A4A", fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 600, fontSize: 14 }}>
        Changes Made
      </h4>

      {renderChanges()}
    </div>
  );
}

export default AdminChangeDetail;
